import * as PIXI from "pixi.js";

import { SingleInstanceClass } from "@src/core/utils/SingleInstanceClass";
import { GameApplication } from "@src/core/game/GameApplication";
import { LoadingScreen } from "@src/game/screens/LoadingScreen";
import { EventSystem } from "@src/core/events/EventSystem";
import { GameScreen } from "@src/game/screens/GameScreen";
import { gameEngine } from "@src/game/GameEngine";
import { constants } from "@src/game/Constants";

import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import gsap from "gsap";
import { Audio } from "./core/audio/Audio";

export type TDeviceType = "mobile" | "desktop";

// This script handles creating game application as well as
// initializing the game screens and the transitions between them
class Game extends SingleInstanceClass {
  private _app: PIXI.Application | undefined;
  private _gameApplication!: GameApplication;

  private _loadingScreen!: LoadingScreen;
  private _gameScreen!: GameScreen;

  private _currentDeviceType!: TDeviceType;

  private _pointerUpListener!: (event: PointerEvent) => void;
  private _keyUpListener!: (event: KeyboardEvent) => void;

  public constructor() {
    super();
  }

  public async start(): Promise<void> {
    gsap.registerPlugin(MotionPathPlugin);

    this._currentDeviceType = this._getDeviceType();

    this._gameApplication = new GameApplication();
    await this._gameApplication.create();
    this._gameApplication.start();

    this._app = this._gameApplication.app;

    this._loadingScreen = await LoadingScreen.load();
    Audio.play("sndIdle", true);

    if (this._app) {
      this._app.renderer.on("resize", this._onResizedViewport.bind(this));
    }

    if (this._app) {
      this._app.stage.addChild(this._loadingScreen.view);
    }

    this._resizeViewport(window.innerWidth, window.innerHeight);

    // We ensure that all the assets for the _loadingScreen are loaded before we proceed
    // to update its loading bar with the _gameScreen asset loading progress
    this._gameScreen = await GameScreen.load((gameScreenProgress: number) => {
      this._loadingScreen.setProgress(gameScreenProgress);
    });

    // Connect to the backend when we are done loading the GameScreen with its components
    // so that our game components can listen to our server-side events.
    await this._connectToServer();
    this._loadingScreen.showContinueMessage();
    this._focusDocument();
    this._setupEventListeners();

    EventSystem.invoke(
      constants.GameEvents.DEVICE_CHANGED,
      this._currentDeviceType,
    );

  }

  private _focusDocument(): void {
    document.body.tabIndex = 0;
    document.body.focus();
  }

  private async _connectToServer(): Promise<void> {
    await gameEngine.start();
  }

  private _transitionToGameScreen(): void {
    if (this._app) {
      this._app.stage.removeChild(this._loadingScreen.view);
      this._app.stage.addChild(this._gameScreen.view);

      this._resizeViewport(window.innerWidth, window.innerHeight);
    }

    LoadingScreen.unload(this._loadingScreen);
    Audio.fade("sndIdle", 0.0, 2.0);

    this._gameScreen.playMainBgMusic();
    Audio.play("sndIntroduction");
  }

  private _setupEventListeners(): void {
    this._pointerUpListener = this._onPointerUp.bind(this);
    this._keyUpListener = this._onKeyUp.bind(this);

    document.addEventListener("pointerup", this._pointerUpListener);
    document.addEventListener("keyup", this._keyUpListener);
  }

  private _onPointerUp(_: PointerEvent): void {
    this._transitionToGameScreen();

    document.removeEventListener("pointerup", this._pointerUpListener);
    document.removeEventListener("keyup", this._keyUpListener);
  }

  private _onKeyUp(event: KeyboardEvent): void {
    if (event.code === "Enter" || event.code === "Space") {
      this._transitionToGameScreen();

      document.removeEventListener("pointerup", this._pointerUpListener);
      document.removeEventListener("keyup", this._keyUpListener);
    }
  }

  private _onResizedViewport(width: number, height: number) {
    this._resizeViewport(width, height);
  }

  private _getDeviceType(): TDeviceType {
    const ua = navigator.userAgent || (window as any).opera;
    const isIos =
      /iPad|iPhone|iPod/.test(ua) ||
      ((navigator as any).platform === "MacIntel" &&
        (navigator as any).maxTouchPoints > 1);
    const isAndroid = /Android/.test(ua);
    const isMobile = /Mobi|Opera Mini|IEMobile/.test(ua) || isIos || isAndroid;

    console.log(`new device is ${isMobile ? "mobile" : "desktop"}`);

    return isMobile ? "mobile" : "desktop";
  }

  private _resizeViewport(width: number, height: number) {
    if (this._app) {
      const ratio: number = width / height;
      let scale: number = 1.0;

      if (ratio > 1.4) {
        EventSystem.invoke(constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED);
        scale = height / constants.Screen.HEIGHT_LANDSCAPE;
      } else {
        EventSystem.invoke(constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED);
        scale = height / constants.Screen.HEIGHT_PORTRAIT;
      }

      this._app.stage.scale.set(scale);
      this._app.stage.x = width / 2;
      this._app.stage.y = height / 2;

      // Fire a system-wide resize event for all game components.
      EventSystem.invoke(constants.GameEvents.VIEWPORT_RESIZED, width, height);

      const newDeviceType: TDeviceType = this._getDeviceType();

      if (newDeviceType === this._currentDeviceType) {
        return;
      }

      this._currentDeviceType = newDeviceType;
      EventSystem.invoke(constants.GameEvents.DEVICE_CHANGED, newDeviceType);
    }
  }
}

// Entry point.
(async () => {
  await new Game().start();
})();
